import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck,
  AlertCircle,
  Target,
  User,
  Undo2,
  TrendingUp,
  TrendingDown,
} from "lucide-react";

const CONFIDENCE_STYLES = {
  high: { text: "text-emerald-700", bg: "bg-emerald-500/10", border: "border-emerald-500/40" },
  medium: { text: "text-amber-700", bg: "bg-amber-500/10", border: "border-amber-500/40" },
  low: { text: "text-rose-700", bg: "bg-rose-500/10", border: "border-rose-500/40" },
};

export default function ApprovalEvidencePackage({ pkg }) {
  if (!pkg) return null;
  const conf = CONFIDENCE_STYLES[pkg.confidence_label] || CONFIDENCE_STYLES.low;
  const confPct = Math.round((pkg.confidence || 0) * 100);

  return (
    <div
      data-testid="approval-evidence-package"
      className="cre-surface rounded-sm p-5 md:p-6"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="cre-overline text-emerald-700">Approval evidence package</div>
          <h3 className="font-heading text-xl font-bold tracking-tight mt-1">
            Review before approval
          </h3>
          <p className="mt-1 text-xs text-slate-500 max-w-2xl">
            Human approval is mandatory. No automatic execution. This card is
            the consolidated evidence reviewers should weigh before
            approving, rejecting, or requesting more review.
          </p>
        </div>
        <Badge
          variant="outline"
          data-testid="approval-confidence-badge"
          className={`rounded-sm font-mono uppercase text-[10px] tracking-wider ${conf.border} ${conf.text} ${conf.bg}`}
        >
          {pkg.confidence_label} confidence · {confPct}%
        </Badge>
      </div>

      <div className="mt-4 space-y-3">
        <Row
          icon={Target}
          tone="text-emerald-700"
          label="Recommended action"
          testId="ae-recommended-action"
          value={pkg.recommended_action}
        />
        <Row
          icon={ShieldCheck}
          tone="text-sky-700"
          label="Why recommended"
          testId="ae-why-recommended"
          value={pkg.why_recommended}
        />

        {pkg.supporting_evidence?.length ? (
          <div
            data-testid="ae-supporting-evidence"
            className="border border-slate-200 bg-slate-50/60 rounded-sm p-3"
          >
            <div className="cre-overline text-slate-500 mb-1.5 flex items-center gap-1.5">
              <AlertCircle className="h-3.5 w-3.5" /> Supporting evidence
            </div>
            <ul className="text-sm text-slate-700 list-disc ml-5 space-y-0.5">
              {pkg.supporting_evidence.map((e, i) => (
                <li
                  key={i}
                  data-testid={`ae-evidence-${i}`}
                  className="break-words"
                >
                  {e}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <Row
            icon={TrendingUp}
            tone="text-amber-700"
            label="Risk if taken"
            testId="ae-risk-if-taken"
            value={pkg.risk_if_taken}
            block
          />
          <Row
            icon={TrendingDown}
            tone="text-rose-700"
            label="Risk if NOT taken"
            testId="ae-risk-if-not-taken"
            value={pkg.risk_if_not_taken}
            block
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Row
            icon={User}
            tone="text-violet-700"
            label="Suggested owner"
            testId="ae-suggested-owner"
            value={pkg.suggested_owner}
            block
          />
          <Row
            icon={Undo2}
            tone="text-slate-700"
            label="Rollback / safety note"
            testId="ae-rollback-note"
            value={pkg.rollback_safety_note}
            block
          />
        </div>
      </div>

      <div className="mt-4 text-[11px] font-mono text-slate-500">
        Decisions available: <span className="text-emerald-700">Approve</span> ·{" "}
        <span className="text-rose-700">Reject</span> ·{" "}
        <span className="text-amber-700">Needs more review</span> · Use the
        action buttons below to record your decision.
      </div>
    </div>
  );
}

function Row({ icon: Icon, tone, label, value, testId, block }) {
  if (!value) return null;
  return (
    <div
      data-testid={testId}
      className={`flex ${
        block ? "flex-col" : "items-start"
      } gap-2 border border-slate-200 bg-white rounded-sm p-3`}
    >
      <div className="flex items-center gap-1.5 cre-overline">
        <Icon className={`h-3.5 w-3.5 ${tone}`} strokeWidth={2} />
        <span className={tone}>{label}</span>
      </div>
      <div className="text-sm text-slate-800 leading-snug break-words">
        {value}
      </div>
    </div>
  );
}
